import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { RotateCcw, Pencil, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SOURCE_PILL = {
  db: "bg-brand-success/10 text-brand-success border-brand-success/25",
  env: "bg-stone-100 text-stone-700 border-stone-200",
  default: "bg-amber-50 text-amber-700 border-amber-100",
};

function formatDisplay(item) {
  if (item.type === "bool") return item.value ? "ON" : "OFF";
  if (item.value === "" || item.value === null || item.value === undefined) return "—";
  if (item.type === "float") {
    const prefix = item.ui_hint?.prefix || "";
    return `${prefix}${Number(item.value).toFixed(item.key === "EST_COST_PER_RENDER_AUD" ? 4 : 2)}`;
  }
  return String(item.value);
}

export default function AdminSettings() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const { refresh: refreshPublic } = usePublicSettings();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // catalog item
  const [resetting, setResetting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      m[it.group] = m[it.group] || [];
      m[it.group].push(it);
    });
    return m;
  }, [items]);

  const onSaved = async () => {
    setEditing(null);
    await load();
    await refreshPublic();
  };

  const doReset = async () => {
    if (!resetting) return;
    setBusy(true);
    try {
      await api.post(`/admin/settings/${resetting.key}/reset`);
      toast.success(`${resetting.label} reset to default`);
      setResetting(null);
      await load();
      await refreshPublic();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const GROUP_ORDER = ["Pricing", "Operations", "Company Information", "Feature flags"];
  const groupSlug = (g) => g.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="space-y-8" data-testid="admin-settings">
      {!isOwner && (
        <div
          data-testid="settings-readonly-badge"
          className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm"
        >
          <Lock className="w-4 h-4" />
          Read-only — only the Owner can edit system settings.
        </div>
      )}
      {loading && items.length === 0 && (
        <div className="text-sm text-brand-muted" data-testid="settings-loading">Loading settings…</div>
      )}
      {GROUP_ORDER.filter((g) => groups[g]).map((g) => (
        <section
          key={g}
          data-testid={`settings-group-${groupSlug(g)}`}
          className="bg-white border border-brand-linen"
        >
          <header className="px-5 py-3 border-b border-brand-linen bg-brand-linen/30">
            <div className="overline">{g}</div>
          </header>
          <div className="divide-y divide-brand-linen">
            {groups[g].map((it) => (
              <div
                key={it.key}
                data-testid={`settings-row-${it.key}`}
                className="px-5 py-4 grid grid-cols-12 gap-3 items-center"
              >
                <div className="col-span-12 sm:col-span-5">
                  <div className="text-sm font-medium text-brand-ink">{it.label}</div>
                  <div className="text-xs text-brand-muted mt-1">{it.description}</div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted mt-1">
                    Key · {it.key}{it.public ? " · public" : ""}
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <div className="text-sm text-brand-ink font-mono" data-testid={`settings-value-${it.key}`}>
                    {formatDisplay(it)}
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <span
                    data-testid={`settings-source-${it.key}`}
                    className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${SOURCE_PILL[it.source]}`}
                  >
                    {it.source}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-2 flex gap-2 justify-end">
                  <button
                    data-testid={`settings-edit-${it.key}`}
                    onClick={() => setEditing(it)}
                    disabled={!isOwner}
                    className="p-2 border border-brand-linen text-brand-ink hover:bg-brand-linen/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`settings-reset-${it.key}`}
                    onClick={() => setResetting(it)}
                    disabled={!isOwner || it.source === "default"}
                    className="p-2 border border-brand-linen text-brand-ink hover:bg-brand-linen/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Reset to default"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <EditDialog
        item={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />

      <Dialog open={!!resetting} onOpenChange={(v) => { if (!v) setResetting(null); }}>
        <DialogContent data-testid="dialog-settings-reset" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset to default?</DialogTitle>
            <DialogDescription>
              {resetting && (
                <>
                  This will remove the DB override on <strong>{resetting.label}</strong>.
                  The value will fall back to the environment or built-in default.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetting(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={doReset} disabled={busy} data-testid="dialog-settings-reset-confirm">
              {busy ? "Resetting…" : "Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditDialog({ item, open, onClose, onSaved }) {
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && item) {
      setVal(item.type === "bool" ? !!item.value : String(item.value ?? ""));
      setErr("");
    }
  }, [open, item]);

  if (!item) return null;
  const hint = item.ui_hint || {};

  const parse = () => {
    if (item.type === "bool") return !!val;
    if (item.type === "int") {
      const n = Number(val);
      if (!Number.isInteger(n)) throw new Error("Must be an integer");
      return n;
    }
    if (item.type === "float") {
      const n = Number(val);
      if (!Number.isFinite(n)) throw new Error("Must be a number");
      return n;
    }
    return String(val);
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      const payload = { value: parse() };
      await api.patch(`/admin/settings/${item.key}`, payload);
      toast.success(`${item.label} updated`);
      onSaved();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Client-side preview of validation to disable the save button when obviously invalid.
  let clientValid = true;
  try {
    const v = parse();
    if (item.type === "float" || item.type === "int") {
      if (typeof hint.min === "number" && v < hint.min) clientValid = false;
      if (typeof hint.max === "number" && v > hint.max) clientValid = false;
    }
  } catch { clientValid = false; }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="dialog-settings-edit" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="dialog-settings-edit-title">Edit {item.label}</DialogTitle>
          <DialogDescription>{item.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {item.type === "bool" ? (
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={!!val}
                onChange={(e) => setVal(e.target.checked)}
                data-testid="dialog-settings-edit-input"
                className="w-4 h-4"
              />
              <span className="text-sm">{val ? "ON" : "OFF"}</span>
            </label>
          ) : item.type === "string" && hint.multiline ? (
            <Textarea
              data-testid="dialog-settings-edit-input"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              maxLength={hint.max_length || 1000}
              placeholder={hint.placeholder || ""}
              rows={3}
            />
          ) : item.type === "string" ? (
            <Input
              data-testid="dialog-settings-edit-input"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              maxLength={hint.max_length || 200}
              placeholder={hint.placeholder || ""}
            />
          ) : (
            <Input
              data-testid="dialog-settings-edit-input"
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              min={hint.min}
              max={hint.max}
              step={hint.step ?? (item.type === "int" ? 1 : 0.01)}
            />
          )}
          {item.key === "UNLOCK_PRICE_AUD" && Number(val) > 0 && Math.abs(Number(val) - Number(item.value)) / Math.max(1, Number(item.value)) > 0.5 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
              ⚠ Large price change (&gt;50% from current). Please double-check before saving.
            </div>
          )}
          <div className="text-xs text-brand-muted">
            Default: <span className="font-mono">{String(item.default)}</span> · Source: {item.source}
          </div>
          {err && (
            <div data-testid="dialog-settings-edit-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 p-2">
              {err}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button
            data-testid="dialog-settings-edit-cancel"
            variant="outline"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            data-testid="dialog-settings-edit-submit"
            onClick={submit}
            disabled={busy || !clientValid}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
